import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import EmptyEventsState from "../../../../components/Events/EmptyEventsState";
import messages from "../../../../assets/locale/messages";
import { getEventsRequest } from "../../../../store/Events/actions";
import {
  getCitiesRequest,
  getProjectManagersRequest,
} from "../../../../store/Lookups/actions";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { eventPublishingModes, eventStatuses, lookupsKeys } from "../../../../utils/Constants";
import { textContainOnlySpaces, appendActiveUrlTabBreadcrumb } from "../../../../utils/Helpers";
import { isSupervisorManger } from "../../../../utils/Permissions";
import { extractPortalLookupArray } from "../../../../utils/companyPortal/portalEventsList";
import * as companiesApi from "../../../../network/apis/companies";
import { getLookups } from "../../../../network/apis/lookups";
import {
  UIButton,
  UIPaginationBar,
  UITable,
  UITabs,
} from "../../../../components/ui";
import store from "../../../../store";

import { companyName, nameByLang, useEventsListColumns, usePayrollEventsListColumns } from "./columns";
import EventsListViewFilter from "./EventsListViewFilter";
import { Box } from "@mui/material";

import "./EventsListView.scss";

const rowAcceptedStaff = (row) => Number(row.number_of_approved_requests ?? 0);

const rowRequiredStaff = (row) =>
  Number(row.maximum_number_of_approved_requests ?? 0);

const validEventStatusValues = Object.values(eventStatuses);

const REPORTS_TAB_VALUES = [
  eventStatuses.ongoing,
  eventStatuses.upcoming,
  eventStatuses.past,
];

const getStatusFromSearch = (search, allowedValues = validEventStatusValues) => {
  const tab = new URLSearchParams(search).get("tab");
  return allowedValues.includes(tab) ? tab : null;
};

const EventsListView = ({ destination }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isReportsList = location.pathname === ROUTE_PATHS.reports;
  const isLogisticsStyleList = isReportsList;
  const allowedTabValues = isReportsList
    ? REPORTS_TAB_VALUES
    : validEventStatusValues;
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.events.list);
  const meta = useSelector((state) => state.events.meta);
  const cities = useSelector((state) => state.lookups.cities);
  const projectManagersList = useSelector(
    (state) => state.lookups.projectManagers,
  );
  const { events, shared } = messages[lang];
  const labels = events.listEvents;
  const creatEventLabels = events.creatEvent;

  const [activeStatus, setActiveStatus] = useState(
    getStatusFromSearch(location.search, allowedTabValues) ||
      location?.state ||
      eventStatuses.ongoing,
  );
  const activeStatusRef = useRef(activeStatus);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCrewSize, setSelectedCrewSize] = useState(null);
  const [crewSizeOptions, setCrewSizeOptions] = useState([]);
  const [selectedProjectManager, setSelectedProjectManager] = useState(null);
  const [selectedPublishingMode, setSelectedPublishingMode] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);

  const endDateBeforeStart =
    !!startDate && !!endDate && dayjs(endDate).isBefore(dayjs(startDate), "day");

  const tabs = useMemo(() => {
    if (isReportsList) {
      return [
        { value: eventStatuses.ongoing, label: labels.eventTabs.ongoing },
        { value: eventStatuses.upcoming, label: labels.eventTabs.upcoming },
        { value: eventStatuses.past, label: labels.eventTabs.previous },
      ];
    }

    return [
      { value: eventStatuses.ongoing, label: labels.eventTabs.ongoing },
      { value: eventStatuses.upcoming, label: labels.eventTabs.upcoming },
      { value: eventStatuses.past, label: labels.eventTabs.previous },
      { value: eventStatuses.draft, label: labels.eventTabs.draft },
      // Publishing request submitted by the company, waiting on the Super Admin.
      { value: eventStatuses.pendingPayment, label: labels.eventTabs.pending },
      { value: eventStatuses.cancelled, label: labels.eventTabs.cancelled },
    ];
  }, [isReportsList, labels]);

  const publishingModeOptions = useMemo(
    () => [
      {
        id: 1,
        name: creatEventLabels.private,
        value: eventPublishingModes.private,
      },
      {
        id: 2,
        name: creatEventLabels.public,
        value: eventPublishingModes.public,
      },
    ],
    [creatEventLabels.private, creatEventLabels.public],
  );

  const payrollColumnLabels = useMemo(
    () => ({
      eventsColumn: shared.events,
      projectManagers: labels.projectManagers,
      type: labels.type,
      startDate: labels.startDate,
      endDate: labels.endDate,
      publishMode: labels.publishMode,
      daily: labels.daily,
      monthly: labels.monthly,
    }),
    [labels, shared.events],
  );

  const pageTitle = isReportsList
    ? shared.listBreadCrumbs.listReportsBreadcrumb
    : labels.listEventsBreadcrumb;

  const listTabLabels = useMemo(
    () => Object.fromEntries(tabs.map((tab) => [tab.value, tab.label])),
    [tabs],
  );

  const breadcrumbList = useMemo(
    () =>
      appendActiveUrlTabBreadcrumb({
        breadcrumbs: [{ path: "", label: pageTitle }],
        activeTab: activeStatus,
        defaultTab: eventStatuses.ongoing,
        tabLabelsByValue: listTabLabels,
        viewPathname: location.pathname,
        search: location.search,
      }),
    [
      activeStatus,
      listTabLabels,
      location.pathname,
      location.search,
      pageTitle,
    ],
  );

  useEffect(() => {
    const t = setTimeout(() => setSearchValue(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const isSupervisor = isSupervisorManger();

  useEffect(() => {
    // Supervisor sessions use user_app tokens — admin_portal lookups 401 and log them out.
    if (isSupervisor) return;
    dispatch(getCitiesRequest({ page: -1 }));
  }, [dispatch, lang, isSupervisor]);

  useEffect(() => {
    if (isSupervisor) return;
    if (isLogisticsStyleList) {
      dispatch(getProjectManagersRequest({ page: -1 }));
    }
  }, [dispatch, isLogisticsStyleList, lang, isSupervisor]);

  useEffect(() => {
    const isReports = location.pathname === ROUTE_PATHS.reports;
    const allowed = isReports
      ? REPORTS_TAB_VALUES
      : validEventStatusValues;
    const tabFromUrl = getStatusFromSearch(location.search, allowed);

    setActiveStatus(tabFromUrl || eventStatuses.ongoing);
    setPage(1);
    setSearchInput("");
    setSelectedCompany(null);
    setSelectedCity(null);
    setSelectedCrewSize(null);
    setSelectedProjectManager(null);
    setSelectedPublishingMode(null);
    setStartDate("");
    setEndDate("");
    setClearFilters(true);
  }, [location.pathname]);

  useEffect(() => {
    if (isSupervisor) {
      setCompanyOptions([]);
      setCompaniesLoading(false);
      return undefined;
    }
    let active = true;
    setCompaniesLoading(true);
    companiesApi
      .getCompanies({ page: 1, items: 50, status: "approved,deactivated" })
      .then((response) => {
        if (!active) return;
        setCompanyOptions(
          (response?.data?.data || []).map((company) => ({
            ...company,
            name: companyName(company, lang),
            value: company.id,
          })),
        );
      })
      .catch(() => {
        if (active) setCompanyOptions([]);
      })
      .finally(() => {
        if (active) setCompaniesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [lang, isSupervisor]);

  // The names come back in the request locale.
  useEffect(() => {
    if (isSupervisor || isLogisticsStyleList) return undefined;
    let active = true;
    getLookups([lookupsKeys.crewSizePlans])
      .then((response) => {
        if (!active) return;
        setCrewSizeOptions(
          extractPortalLookupArray(response, lookupsKeys.crewSizePlans).map((plan) => ({
            id: plan.id,
            name: plan.name,
            value: plan.id,
          })),
        );
      })
      .catch(() => {
        if (active) setCrewSizeOptions([]);
      });
    return () => {
      active = false;
    };
  }, [lang, isSupervisor, isLogisticsStyleList]);

  useEffect(() => {
    if (clearFilters) setClearFilters(false);
  }, [clearFilters]);

  const cityOptions = useMemo(
    () =>
      (cities || []).map((city) => ({
        ...city,
        name: nameByLang(city, lang),
        value: city.id,
      })),
    [cities, lang],
  );

  useEffect(() => {
    activeStatusRef.current = activeStatus;
  }, [activeStatus]);

  useEffect(() => {
    const nextStatus = getStatusFromSearch(location.search, allowedTabValues);
    if (nextStatus) {
      setActiveStatus(nextStatus);
      setPage(1);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.set("tab", activeStatusRef.current);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [allowedTabValues, history, location.pathname, location.search]);

  useEffect(() => {
    if (textContainOnlySpaces(searchInput)) return;
    if (searchValue.length > 0 && searchValue.length < 3) return;
    if (endDateBeforeStart) return;
    const supervisorId = store.getState().auth.login?.id;
    dispatch(
      getEventsRequest({
        page,
        items: pageSize,
        query: searchValue,
        status: activeStatus,
        company_id:
          isSupervisor || isLogisticsStyleList
            ? null
            : selectedCompany?.id || null,
        city_id:
          isSupervisor || isLogisticsStyleList
            ? null
            : selectedCity?.id || null,
        crew_size_plan_id:
          isSupervisor || isLogisticsStyleList
            ? null
            : selectedCrewSize?.id || null,
        event_start_date: startDate || null,
        event_end_date: endDate || null,
        project_manager_id: isLogisticsStyleList
          ? selectedProjectManager?.id || null
          : null,
        publish_mode: isLogisticsStyleList
          ? selectedPublishingMode?.value || null
          : null,
        ...(isSupervisor && supervisorId != null
          ? { supervisor_id: supervisorId }
          : {}),
      }),
    );
  }, [
    dispatch,
    page,
    pageSize,
    searchInput,
    searchValue,
    activeStatus,
    selectedCompany,
    selectedCity,
    selectedCrewSize,
    selectedProjectManager,
    selectedPublishingMode,
    startDate,
    endDate,
    endDateBeforeStart,
    isLogisticsStyleList,
    isSupervisor,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    pageSize,
    searchValue,
    activeStatus,
    selectedCompany,
    selectedCity,
    selectedCrewSize,
    selectedProjectManager,
    selectedPublishingMode,
    startDate,
    endDate,
    isLogisticsStyleList,
  ]);

  const rows = useMemo(
    () =>
      (list || []).map((row) => ({
        ...row,
        id: row.id,
        _company: row.company,
        _acceptedStaff: rowAcceptedStaff(row),
        _requiredStaff: rowRequiredStaff(row),
      })),
    [list],
  );

  const visibleRows = useMemo(
    () => rows.filter((row) => row.status === activeStatus),
    [activeStatus, rows],
  );

  const hasStatusLeak = rows.length > 0 && visibleRows.length !== rows.length;
  const tableRows = hasStatusLeak ? visibleRows : rows;
  const paginationMeta = hasStatusLeak
    ? {
        currentPage: 1,
        pages: 1,
        count: visibleRows.length,
      }
    : meta || {};

  const handleRowClick = ({ row }) => {
    if (location.pathname === ROUTE_PATHS.requestsEvents) {
      history.push(ROUTE_PATHS.viewRequests.replace(":id", row.id));
      return;
    }

    if (isReportsList) {
      history.push(ROUTE_PATHS.viewReport.replace(":id", row.id));
      return;
    }

    const viewPath =
      destination === "adminClientEventSurvey"
        ? ROUTE_PATHS.adminClientEventSurvey
        : destination === "clientSurvey"
        ? ROUTE_PATHS.clientSurveyByID
        : destination === "eventSurvey"
        ? ROUTE_PATHS.viewEventSurvey
        : destination === "eventHeatmap"
        ? ROUTE_PATHS.viewEventHeatMap
        : ROUTE_PATHS.viewEvent;

    history.push(viewPath.replace(":id", row.id));
  };

  const eventColumns = useEventsListColumns(
    labels,
    lang,
    isSupervisor ? undefined : creatEventLabels.crewSize.stepLabel,
  );
  const payrollColumns = usePayrollEventsListColumns(
    payrollColumnLabels,
    creatEventLabels,
  );
  const columns = isLogisticsStyleList ? payrollColumns : eventColumns;

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const from = paginationMeta?.count
    ? ((paginationMeta.currentPage || page) - 1) * pageSize + 1
    : 0;
  const to = Math.min(
    (paginationMeta.currentPage || page) * pageSize,
    paginationMeta?.count || 0,
  );

  const showCreateEventAction =
    !isSupervisor &&
    !isReportsList &&
    location.pathname === ROUTE_PATHS.events &&
    !destination;

  const renderBody = () => {
    if (!isLoading && tableRows.length === 0) {
      return (
        <EmptyEventsState
          title={labels.emptyTitle}
          description={labels.noDataDescription}
          actionLabel={showCreateEventAction ? labels.emptyAction : undefined}
          onCreate={
            showCreateEventAction
              ? () => history.push(ROUTE_PATHS.createEvent)
              : undefined
          }
        />
      );
    }

    return (
      <>
        <UITable
          rows={tableRows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          rowHeight={72}
          hideFooter
          onRowClick={handleRowClick}
          getRowClassName={() => "events-list-view__row"}
        />
        {paginationMeta?.count > 0 && (
          <UIPaginationBar
            currentPage={paginationMeta.currentPage || page}
            pages={paginationMeta.pages || 1}
            pageSize={pageSize}
            rangeTemplate={labels.showingEventsRange}
            rangeFrom={from}
            rangeTo={to}
            rangeTotal={paginationMeta.count || 0}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            className="events-list-view__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="events-list-view">
      <div className="page-title-margin">
        <Breadcrumbs
          list={breadcrumbList}
          BreadCrumbHasBackIcon={false}
        />
      </div>

      <Box className="events-list-view__tabs-row">
        <UITabs
          tabs={tabs}
          value={activeStatus}
          onChange={(next) => {
            const params = new URLSearchParams(location.search);
            params.set("tab", next);
            history.replace({
              pathname: location.pathname,
              search: params.toString(),
            });
            setActiveStatus(next);
            setPage(1);
            setSearchInput("");
            setSelectedCompany(null);
            setSelectedCity(null);
            setSelectedCrewSize(null);
            setSelectedProjectManager(null);
            setSelectedPublishingMode(null);
            setStartDate("");
            setEndDate("");
            setClearFilters(true);
          }}
          aria-label={pageTitle}
          classNames={{ root: "w-100 events-list-view__tabs" }}
        />

        {showCreateEventAction ? (
          <UIButton
            size="large"
            appearance="gradient"
            type="button"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => history.push(ROUTE_PATHS.createEvent)}
          >
            {labels.createEvent}
          </UIButton>
        ) : null}
      </Box>

      <EventsListViewFilter
        labels={labels}
        isPayroll={isLogisticsStyleList}
        hideAdminFilters={isSupervisor}
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        selectedCompany={selectedCompany}
        onCompanyChange={setSelectedCompany}
        companyOptions={companyOptions}
        companiesLoading={companiesLoading}
        selectedCity={selectedCity}
        onCityChange={setSelectedCity}
        cityOptions={cityOptions}
        crewSizeLabel={creatEventLabels.crewSize.stepLabel}
        // The selected plan follows the language of the options.
        selectedCrewSize={
          crewSizeOptions.find((option) => option.id === selectedCrewSize?.id) ?? selectedCrewSize
        }
        onCrewSizeChange={setSelectedCrewSize}
        crewSizeOptions={crewSizeOptions}
        selectedProjectManager={selectedProjectManager}
        onProjectManagerChange={setSelectedProjectManager}
        projectManagerOptions={projectManagersList || []}
        selectedPublishingMode={selectedPublishingMode}
        onPublishingModeChange={setSelectedPublishingMode}
        publishingModeOptions={publishingModeOptions}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        clearFilters={clearFilters}
        endDateBeforeStart={endDateBeforeStart}
      />

      <div className="events-list-view__table-shell">{renderBody()}</div>
    </div>
  );
};

EventsListView.propTypes = {
  destination: PropTypes.string,
};

EventsListView.defaultProps = {
  destination: undefined,
};

export default EventsListView;
